<template>
    <div class="upload-zip-container col-lg-12">
        <div class="stage-info-banner alert alert-info mb-4 d-flex align-items-center justify-content-between flex-wrap">
            <div>
                <h5 class="mb-1 text-primary font-weight-bold"><i class="ri-information-line mr-1"></i>{{ stageTitle }}</h5>
                <span class="text-muted small" v-if="stagePeriodText"> <i class="ri-calendar-line mr-1"></i>対象期間: {{ stagePeriodText }} </span>
            </div>
        </div>

        <!-- Rules Box -->
        <div class="card mb-4 border-info">
            <div class="card-body p-3 bg-soft-info rounded">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
                    <div class="d-flex align-items-center">
                        <i class="ri-file-zip-line text-info font-size-22 mr-2"></i>
                        <h6 class="mb-0 font-weight-bold text-info">
                            {{ $t('landingPage.upload.rulesTitle') || 'ZIPファイル作成ルール' }}
                        </h6>
                    </div>
                    <div class="mt-2 mt-sm-0">
                        <a href="/samples/lp-upload-sample.zip" download class="btn btn-common btn-outline-primary mr-2">
                            <i class="ri-download-2-line mr-1"></i>サンプルZIP（フォルダ構成）
                        </a>
                        <a href="/samples/lp-upload-sample-flat.zip" download class="btn btn-common btn-outline-secondary">
                            <i class="ri-download-2-line mr-1"></i>サンプルZIP（フラット構成）
                        </a>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-lg-7">
                        <ul class="mb-0 pl-3 font-size-13 text-dark">
                            <li class="mb-2">
                                <strong>メインファイル名（必須）:</strong>
                                ZIP直下（root）に必ず <code class="text-danger font-weight-bold">index.html</code> を配置してください。<span
                                    class="text-danger font-weight-bold"
                                    >（親フォルダで囲んで圧縮しないでください）</span
                                >
                            </li>
                            <li class="mb-2">
                                <strong>HTML内のパス指定（必須）:</strong>
                                index.html内のCSS・JS・画像などの呼び出しは、必ず<strong>相対パス</strong>で指定してください。
                                <br />
                                <span class="text-muted small"
                                    >例: <code>&lt;link href="./css/style.css"&gt;</code> や <code>&lt;script src="./js/main.js"&gt;</code>、<code
                                        >&lt;img src="./images/banner.png"&gt;</code
                                    ></span
                                >
                            </li>
                            <li class="mb-2">
                                <strong>柔軟なファイル構成:</strong>
                                CSS・JS・画像は index.html と同一階層、または <code>/css</code>, <code>/js</code>,
                                <code>/images</code> などのサブフォルダに自由に配置可能です。
                            </li>
                            <li><strong>ファイル容量:</strong> 最大 50MB までアップロード可能です。</li>
                        </ul>
                    </div>

                    <div class="col-lg-5 mt-3 mt-lg-0">
                        <div class="p-2 bg-white border rounded font-size-11" style="font-family: monospace; line-height: 1.4">
                            <span class="text-muted font-weight-bold">【推奨フォルダ構成例】</span>
                            <pre class="mb-0 mt-1" style="font-size: 11px; color: #2d3748; background: transparent; border: none; padding: 0">
sample.zip
├── index.html   (※root直下に必須)
├── css/
│   └── style.css
├── js/
│   └── main.js
└── images/
    └── banner.png</pre
                            >
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Upload Dropzone (hidden in view mode) -->
        <div v-if="!isView" class="upload-dropzone-card card mb-4">
            <div class="card-body">
                <input
                    ref="zipFileInput"
                    type="file"
                    accept=".zip,application/zip,application/x-zip-compressed"
                    class="d-none"
                    @change="onFileChange"
                />

                <div
                    class="dropzone-area p-4 text-center border-dashed rounded"
                    :class="{ 'border-primary bg-soft-primary': isDragging, 'bg-light': !isDragging }"
                    @dragover.prevent="onDragOver"
                    @dragleave.prevent="onDragLeave"
                    @drop.prevent="onDrop"
                    @click="triggerFileInput"
                    style="cursor: pointer; border: 2px dashed #b8c2cc; transition: all 0.2s"
                >
                    <div v-if="isUploading" class="py-4">
                        <div class="spinner-border text-primary mb-3" role="status" style="width: 3rem; height: 3rem">
                            <span class="sr-only">Uploading...</span>
                        </div>
                        <h6 class="font-weight-bold mb-1">{{ $t('landingPage.upload.uploading') || 'アップロード中...' }}</h6>
                        <p class="text-muted small mb-0">ファイルを解凍してS3にアップロードしています。しばらくお待ちください。</p>
                    </div>

                    <!-- Saved File State (previously uploaded) -->
                    <div v-else-if="hasSavedFile" class="py-3">
                        <div class="mb-2">
                            <i class="ri-file-zip-fill text-primary" style="font-size: 48px"></i>
                        </div>
                        <div class="font-weight-bold font-size-16 text-dark mb-1 text-truncate mx-auto" style="max-width: 600px">
                            {{ savedFileNameDisplay }}
                        </div>
                        <div class="d-flex align-items-center justify-content-center flex-wrap" style="gap: 8px">
                            <span class="badge badge-success px-2 py-1"> <i class="ri-checkbox-circle-fill mr-1"></i>アップロード完了 </span>
                            <span v-if="resourceList.length > 0" class="badge badge-light border text-muted px-2 py-1">
                                リソース: {{ resourceList.length }}件
                            </span>
                        </div>
                        <p class="text-muted small mt-2 mb-0">新しいZIPファイルをドラッグ＆ドロップ、または下のボタンからファイルを変更できます。</p>
                    </div>

                    <!-- Empty State -->
                    <div v-else class="py-3">
                        <div class="mb-3">
                            <i class="ri-upload-cloud-2-line text-primary" style="font-size: 48px"></i>
                        </div>
                        <h5 class="font-weight-bold mb-1 text-dark">
                            {{ $t('landingPage.upload.dragDrop') || 'ZIPファイルをここにドラッグ＆ドロップ、またはクリックして選択' }}
                        </h5>
                        <p class="text-muted small mb-0">.zip 形式（最大 50MB）</p>
                    </div>
                </div>

                <!-- Buttons & file info under dropzone -->
                <div class="mt-3 d-flex align-items-center justify-content-between flex-wrap" style="gap: 12px">
                    <div class="d-flex align-items-center text-truncate mr-2"></div>

                    <div class="d-flex align-items-center flex-wrap" style="gap: 10px">
                        <button type="button" class="btn btn-primary" :disabled="isUploading" @click="triggerFileInput">
                            <i :class="isUploading ? 'ri-loader-4-line spin' : 'ri-upload-2-line'" class="mr-1"></i>
                            {{ isUploading ? 'アップロード中...' : hasSavedFile ? 'ファイルを再アップロード' : 'ファイルをアップロード' }}
                        </button>

                        <button type="button" class="btn btn-outline-danger" :disabled="isUploading || !hasSavedFile" @click="handleDeleteFile">
                            <i class="ri-delete-bin-line mr-1"></i>ファイルを削除
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- View mode: Display uploaded file info if available -->
        <div v-if="isView && uploadedFileNameDisplay" class="card mb-4 bg-light border">
            <div class="card-body p-3 d-flex align-items-center justify-content-between flex-wrap">
                <div class="d-flex align-items-center text-truncate mr-2">
                    <i class="ri-file-zip-fill text-primary font-size-24 mr-2"></i>
                    <div>
                        <span class="text-muted small d-block">アップロード済みファイル:</span>
                        <strong class="text-dark font-size-14 text-truncate">{{ uploadedFileNameDisplay }}</strong>
                    </div>
                </div>
                <span class="badge badge-success px-2 py-1">
                    <i class="ri-checkbox-circle-fill mr-1"></i>アップロード済み ({{ resourceList.length }}件)
                </span>
            </div>
        </div>

        <!-- Preview Section -->
        <div class="preview-section-card card shadow-sm">
            <div class="card-header bg-white d-flex align-items-center justify-content-between flex-wrap py-3 border-bottom">
                <div class="d-flex align-items-center flex-wrap">
                    <i class="ri-eye-line text-primary mr-2 font-size-18"></i>
                    <h5 class="mb-0 font-weight-bold">{{ $t('landingPage.upload.previewTitle') || 'プレビュー' }}</h5>
                    <span v-if="previewHtmlUrl" class="badge badge-success ml-2">HTML検出済み</span>
                    <span v-else class="badge badge-secondary ml-2">未検出</span>
                </div>

                <div v-if="previewHtmlUrl" class="d-flex align-items-center flex-wrap mt-2 mt-sm-0" style="gap: 8px">
                    <div class="btn-group btn-group-sm mr-2" role="group">
                        <button
                            type="button"
                            class="btn"
                            :class="deviceMode === 'pc' ? 'btn-primary' : 'btn-outline-secondary'"
                            @click="deviceMode = 'pc'"
                        >
                            <i class="ri-computer-line mr-1"></i>{{ $t('landingPage.upload.devicePC') || 'PC表示' }}
                        </button>
                        <button
                            type="button"
                            class="btn"
                            :class="deviceMode === 'sp' ? 'btn-primary' : 'btn-outline-secondary'"
                            @click="deviceMode = 'sp'"
                        >
                            <i class="ri-smartphone-line mr-1"></i>{{ $t('landingPage.upload.deviceSP') || 'SP表示' }}
                        </button>
                    </div>

                    <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        @click="reloadIframe"
                        v-b-tooltip.hover
                        title="プレビュー再読み込み"
                    >
                        <i class="ri-refresh-line"></i>
                    </button>

                    <button type="button" class="btn btn-sm btn-outline-primary" @click="openInNewTab">
                        <i class="ri-external-link-line mr-1"></i>{{ $t('landingPage.upload.openNewTab') || '新しいタブで開く' }}
                    </button>
                </div>
            </div>

            <div class="card-body p-0" style="background-color: #f4f5f7; min-height: 550px">
                <!-- Has Preview HTML -->
                <div v-if="previewHtmlUrl" class="preview-viewport-wrapper d-flex justify-content-center p-3">
                    <!-- SP Mode simulation -->
                    <div
                        v-if="deviceMode === 'sp'"
                        class="sp-frame-container shadow-lg bg-white rounded"
                        style="width: 375px; max-width: 100%; border: 12px solid #2d3748; border-radius: 36px !important; overflow: hidden"
                    >
                        <div class="sp-notch bg-dark text-white text-center py-1 font-size-11" style="border-bottom: 1px solid #4a5568">
                            <i class="ri-cellphone-line mr-1"></i>Mobile View (375px)
                        </div>
                        <iframe ref="previewIframe" :src="iframeSrc" style="width: 100%; height: 667px; border: none; display: block"></iframe>
                    </div>

                    <!-- PC Mode -->
                    <div v-else class="pc-frame-container shadow-sm bg-white rounded w-100" style="border: 1px solid #e2e8f0; overflow: hidden">
                        <iframe ref="previewIframe" :src="iframeSrc" style="width: 100%; height: 750px; border: none; display: block"></iframe>
                    </div>
                </div>

                <!-- No Preview Empty State -->
                <div v-else class="text-center py-5 text-muted">
                    <div class="mb-3">
                        <i class="ri-file-search-line text-secondary font-size-48"></i>
                    </div>
                    <h6 class="font-weight-bold text-dark">
                        {{ $t('landingPage.upload.noPreview') || 'プレビュー可能なHTMLファイルが見つかりません。' }}
                    </h6>
                    <p class="small text-muted mb-0 max-w-500 mx-auto">
                        ZIPファイルをアップロードすると、ここに自動的にプレビューが表示されます。<br />
                        ZIPのルート直下に「index.html」が存在することを確認してください。
                    </p>
                </div>
            </div>

            <!-- Uploaded resources accordion / list -->
            <div v-if="resourceList.length > 0" class="card-footer bg-white border-top">
                <div class="d-flex align-items-center justify-content-between">
                    <span class="font-weight-bold font-size-13 text-secondary">
                        <i class="ri-folder-2-line mr-1"></i>
                        {{ $t('landingPage.upload.uploadedResources') || 'アップロード済みリソース' }} ({{ resourceList.length }}件)
                    </span>
                    <button type="button" class="btn btn-sm btn-link text-muted p-0" @click="showResourceList = !showResourceList">
                        {{ showResourceList ? '閉じる' : '一覧を表示' }}
                        <i :class="showResourceList ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                    </button>
                </div>

                <div v-if="showResourceList" class="mt-3 table-responsive" style="max-height: 200px; overflow-y: auto">
                    <table class="table table-sm table-hover mb-0 font-size-12">
                        <tbody>
                            <tr v-for="(url, idx) in resourceList" :key="idx">
                                <td style="width: 30px">
                                    <i :class="getFileIcon(url)" class="text-primary mr-1"></i>
                                </td>
                                <td class="text-truncate" style="max-width: 450px">
                                    <a :href="url" target="_blank" class="text-dark">{{ getFileName(url) }}</a>
                                </td>
                                <td class="text-right">
                                    <a :href="url" target="_blank" class="btn btn-xs btn-outline-secondary py-0">
                                        <i class="ri-external-link-line mr-1"></i>表示
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="mt-3">
            <span class="text-muted small">
                {{
                    $t('landingPage.upload.syncPublishNote') ||
                    '※ZIPファイルをアップロードした時点では本番サイトに反映されません。「本番サイト更新」をクリック後に反映されます。'
                }}
            </span>
        </div>
    </div>
</template>

<script>
import { landingPageMethods } from '@/state/helpers';
import { onHandleShowConfirm } from '@/utils/handlers';
import { showMessage } from '@/utils/messages';
import { formatDateCommon } from '@/utils/format';

export default {
    name: 'UploadZipLP',
    props: {
        dataForm: {
            type: Object,
            required: true,
            default: () => ({})
        },
        stage: {
            type: Number,
            default: 1
        },
        period: {
            type: String,
            default: ''
        },
        title: {
            type: String,
            default: ''
        },
        htmlFileName: {
            type: String,
            default: 'index.html'
        },
        isView: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isDragging: false,
            isUploading: false,
            isPublishing: false,
            selectedFile: null,
            selectedFileName: '',
            selectedFileSize: '',
            uploadedFileName: '',
            deviceMode: 'pc',
            showResourceList: false,
            previewKey: Date.now()
        };
    },
    computed: {
        computedPeriod() {
            if (this.period) return this.period;
            if (this.stage === 1) return 'start';
            if (this.stage === 2) return 'going';
            if (this.stage === 3) return 'end';
            return 'start';
        },
        stageTitle() {
            if (this.title) return this.title;
            if (this.computedPeriod === 'start' || this.stage === 1) return 'LP開始 〜 キャンペーン開始の期間に表示するページ';
            if (this.computedPeriod === 'going' || this.stage === 2) return 'キャンペーン開始 〜 キャンペーン終了の期間に表示するページ';
            if (this.computedPeriod === 'end' || this.stage === 3) return 'キャンペーン終了 〜 LP終了の期間に表示するページ';
            return 'Landing Page HTMLアップロード';
        },
        stagePeriodText() {
            if (!this.dataForm) return '';
            if (this.computedPeriod === 'start' || this.stage === 1) {
                const start = this.formatDateTimeJP(this.dataForm.lp_start_date, this.dataForm.lp_start_time);
                const end = this.formatDateTimeJP(this.dataForm.campaign_start_date, this.dataForm.campaign_start_time);
                return start && end ? `${start} 〜 ${end}` : start || end || '';
            }
            if (this.computedPeriod === 'going' || this.stage === 2) {
                const start = this.formatDateTimeJP(this.dataForm.campaign_start_date, this.dataForm.campaign_start_time);
                const end = this.formatDateTimeJP(this.dataForm.campaign_end_date, this.dataForm.campaign_end_time);
                return start && end ? `${start} 〜 ${end}` : start || end || '';
            }
            if (this.computedPeriod === 'end' || this.stage === 3) {
                const start = this.formatDateTimeJP(this.dataForm.campaign_end_date, this.dataForm.campaign_end_time);
                const end = this.formatDateTimeJP(this.dataForm.lp_end_date, this.dataForm.lp_end_time);
                return start && end ? `${start} 〜 ${end}` : start || end || '';
            }
            return '';
        },
        savedFileNameDisplay() {
            if (!this.hasResources) return '';
            if (this.uploadedFileName) return this.uploadedFileName;
            const period = this.computedPeriod;
            const key = `zip_file_name_${period}`;
            if (this.dataForm && this.dataForm[key]) return this.dataForm[key];
            if (this.dataForm?.id) {
                try {
                    const saved = localStorage.getItem(`lp_${this.dataForm.id}_zip_name_${period}`);
                    if (saved) return saved;
                } catch (e) {
                    // ignore
                }
            }
            return `lp_${period}.zip`;
        },
        uploadedFileNameDisplay() {
            return this.savedFileNameDisplay;
        },
        hasSavedFile() {
            return this.hasResources;
        },
        resourceList() {
            const period = this.computedPeriod;
            if (period === 'start') {
                return Array.isArray(this.dataForm?.resources_zip_start) ? this.dataForm.resources_zip_start : [];
            }
            if (period === 'going') {
                return Array.isArray(this.dataForm?.resources_zip_going) ? this.dataForm.resources_zip_going : [];
            }
            if (period === 'end') {
                return Array.isArray(this.dataForm?.resources_zip_end) ? this.dataForm.resources_zip_end : [];
            }
            return [];
        },
        hasResources() {
            return this.resourceList.length > 0;
        },
        previewHtmlUrl() {
            if (!this.resourceList || this.resourceList.length === 0) return '';

            // Priority 1: match /index.html (strictly requested rule)
            const matchedIndex = this.resourceList.find((url) => {
                const clean = url.split('?')[0].toLowerCase();
                return clean.endsWith('/index.html') || clean === 'index.html';
            });
            if (matchedIndex) return matchedIndex;

            // Priority 2: match custom target if specified and differs
            if (this.htmlFileName && this.htmlFileName !== 'index.html') {
                const targetName = this.htmlFileName.toLowerCase();
                const matchedTarget = this.resourceList.find((url) => {
                    const clean = url.split('?')[0].toLowerCase();
                    return clean.endsWith('/' + targetName) || clean === targetName;
                });
                if (matchedTarget) return matchedTarget;
            }

            // Priority 3: fallback to stage index (index0.html, index1.html)
            const stageFallback = this.resourceList.find((url) => {
                const clean = url.split('?')[0].toLowerCase();
                return clean.endsWith('/index0.html') || clean.endsWith('/index1.html');
            });
            if (stageFallback) return stageFallback;

            // Priority 4: any .html file
            const anyHtml = this.resourceList.find((url) => url.split('?')[0].toLowerCase().endsWith('.html'));
            return anyHtml || '';
        },
        iframeSrc() {
            if (!this.previewHtmlUrl) return '';
            const separator = this.previewHtmlUrl.includes('?') ? '&' : '?';
            return `${this.previewHtmlUrl}${separator}_t=${this.previewKey}`;
        }
    },
    watch: {
        computedPeriod: {
            handler(newPeriod) {
                const key = `zip_file_name_${newPeriod}`;
                if (this.dataForm && this.dataForm[key]) {
                    this.uploadedFileName = this.dataForm[key];
                } else if (this.dataForm?.id && this.hasResources) {
                    try {
                        const saved = localStorage.getItem(`lp_${this.dataForm.id}_zip_name_${newPeriod}`);
                        this.uploadedFileName = saved || '';
                    } catch (e) {
                        this.uploadedFileName = '';
                    }
                } else {
                    this.uploadedFileName = '';
                }
            },
            immediate: true
        }
    },
    methods: {
        ...landingPageMethods,

        formatDateTimeJP(date, time) {
            if (!date) return '';
            let dateStr = String(date).trim();
            if (dateStr.includes('/')) {
                dateStr = dateStr.replace(/\//g, '-');
            }
            const hasTimeInDate = dateStr.includes(' ') || dateStr.includes('T');
            const hasExplicitTime = time !== undefined && time !== null && String(time).trim() !== '';

            if (hasTimeInDate) {
                return formatDateCommon(dateStr, 'dateTimeJP');
            }
            if (hasExplicitTime) {
                const fullStr = `${dateStr} ${String(time).trim()}`;
                return formatDateCommon(fullStr, 'dateTimeJP');
            }
            return formatDateCommon(dateStr, 'dateJP') || dateStr;
        },

        triggerFileInput() {
            if (this.isUploading) return;
            this.$refs.zipFileInput?.click();
        },

        onDragOver() {
            this.isDragging = true;
        },

        onDragLeave() {
            this.isDragging = false;
        },

        onDrop(e) {
            this.isDragging = false;
            const files = e.dataTransfer?.files;
            if (files && files.length > 0) {
                this.validateAndSetFile(files[0]);
            }
        },

        onFileChange(e) {
            const files = e.target?.files;
            if (files && files.length > 0) {
                this.validateAndSetFile(files[0]);
            }
        },

        async validateAndSetFile(file) {
            if (!file) return;

            // Validate extension
            const isZip = file.name.toLowerCase().endsWith('.zip') || file.type.includes('zip');
            if (!isZip) {
                this.$bvToast.toast('ZIP形式（.zip）のファイルを選択してください。', {
                    title: 'ファイルの形式が正しくありません。',
                    variant: 'danger',
                    toaster: 'b-toaster-top-center',
                    solid: true
                });
                return;
            }

            // Validate max size 50MB
            const maxSize = 50 * 1024 * 1024;
            if (file.size > maxSize) {
                this.$bvToast.toast('ファイルサイズは50MB以下にしてください。', {
                    title: 'ファイルサイズは50MB以内のファイルをアップロードしてください。',
                    variant: 'danger',
                    toaster: 'b-toaster-top-center',
                    solid: true
                });
                return;
            }

            this.selectedFile = file;
            this.selectedFileName = file.name;
            this.selectedFileSize = `${(file.size / (1024 * 1024)).toFixed(2)} MB`;

            await this.executeUpload();
        },

        async executeUpload() {
            if (!this.selectedFile) return;
            if (!this.dataForm?.id) {
                this.$bvToast.toast('LPの基本設定を先に登録してください。', {
                    title: '注意',
                    variant: 'warning',
                    toaster: 'b-toaster-top-center',
                    solid: true
                });
                return;
            }

            this.isUploading = true;
            try {
                const formData = new FormData();
                formData.append('file', this.selectedFile);
                formData.append('period', this.computedPeriod);
                formData.append('stage', this.stage);
                formData.append('phase', this.stage);

                const response = await this.uploadZipLandingPage({
                    id: this.dataForm.id,
                    formData: formData
                });

                if (!response || (response.code && response.code !== 200 && response.code !== 201) || response.status === false) {
                    const errorMsg =
                        response?.message ||
                        response?.error ||
                        (response?.errors ? Object.values(response.errors).flat().join(', ') : '') ||
                        'ZIPファイルのアップロードに失敗しました。';
                    throw new Error(errorMsg);
                }

                const resData = response?.data || response;

                // Update 3 period lists
                if (resData?.resources_zip_start !== undefined) {
                    this.$set(this.dataForm, 'resources_zip_start', resData.resources_zip_start || []);
                }
                if (resData?.resources_zip_going !== undefined) {
                    this.$set(this.dataForm, 'resources_zip_going', resData.resources_zip_going || []);
                }
                if (resData?.resources_zip_end !== undefined) {
                    this.$set(this.dataForm, 'resources_zip_end', resData.resources_zip_end || []);
                }

                const fileName = this.selectedFileName || this.selectedFile?.name || '';
                this.uploadedFileName = fileName;
                const period = this.computedPeriod;
                if (fileName) {
                    this.$set(this.dataForm, `zip_file_name_${period}`, fileName);
                    this.$set(this.dataForm, 'zip_file_name', fileName);
                    if (this.dataForm?.id) {
                        try {
                            localStorage.setItem(`lp_${this.dataForm.id}_zip_name_${period}`, fileName);
                        } catch (e) {
                            console.error(e);
                        }
                    }
                }

                this.previewKey = Date.now();

                this.$bvToast.toast('ZIPファイルが正常にアップロードされました。', {
                    title: 'インポートが完了しました。',
                    variant: 'success',
                    toaster: 'b-toaster-top-center',
                    solid: true
                });

                this.$nextTick(() => {
                    this.reloadIframe();
                });
            } catch (error) {
                console.error('Upload zip error:', error);
                let errorMessage = 'ZIPファイルのアップロードに失敗しました。';
                if (error?.message) {
                    errorMessage = error.message;
                } else if (error?.error) {
                    errorMessage = error.error;
                } else if (error?.errors) {
                    const firstKey = Object.keys(error.errors)[0];
                    if (firstKey && error.errors[firstKey]?.length) {
                        errorMessage = error.errors[firstKey][0];
                    }
                } else if (typeof error === 'string') {
                    errorMessage = error;
                }
                this.$bvToast.toast(errorMessage, {
                    title: 'インポートに失敗しました。',
                    variant: 'danger',
                    toaster: 'b-toaster-top-center',
                    solid: true
                });
            } finally {
                this.selectedFile = null;
                this.selectedFileName = '';
                this.selectedFileSize = '';
                this.isUploading = false;
                if (this.$refs.zipFileInput) {
                    this.$refs.zipFileInput.value = '';
                }
            }
        },

        async handleDeleteFile() {
            if (!this.hasSavedFile) return;

            const confirm = await onHandleShowConfirm({
                title: '',
                text: 'アップロード済みのZIPファイルとリソースを削除しますか？\n（プレビューも同時にクリアされます）'
            });
            if (!confirm) return;

            const period = this.computedPeriod;
            if (period === 'start') {
                this.$set(this.dataForm, 'resources_zip_start', []);
            } else if (period === 'going') {
                this.$set(this.dataForm, 'resources_zip_going', []);
            } else if (period === 'end') {
                this.$set(this.dataForm, 'resources_zip_end', []);
            }
            this.$set(this.dataForm, `zip_file_name_${period}`, '');
            if (this.dataForm?.id) {
                try {
                    localStorage.removeItem(`lp_${this.dataForm.id}_zip_name_${period}`);
                } catch (e) {
                    console.error(e);
                }
            }

            this.selectedFile = null;
            this.selectedFileName = '';
            this.selectedFileSize = '';
            this.uploadedFileName = '';

            if (this.$refs.zipFileInput) {
                this.$refs.zipFileInput.value = '';
            }

            this.previewKey = Date.now();

            this.$bvToast.toast('ファイルを削除しました。', {
                title: 'ファイルを削除しました。',
                variant: 'success',
                toaster: 'b-toaster-top-center',
                solid: true,
                autoHideDelay: 2500
            });
        },

        reloadIframe() {
            this.previewKey = Date.now();
            if (this.$refs.previewIframe) {
                const currentSrc = this.$refs.previewIframe.src;
                this.$refs.previewIframe.src = '';
                setTimeout(() => {
                    if (this.$refs.previewIframe) {
                        this.$refs.previewIframe.src = currentSrc;
                    }
                }, 50);
            }
        },

        openInNewTab() {
            if (this.previewHtmlUrl) {
                window.open(this.previewHtmlUrl, '_blank');
            }
        },

        async onHandleSyncLandingPage() {
            if (!this.dataForm?.id) return;
            const confirmSync = await onHandleShowConfirm({
                title: '',
                text: '最新の更新内容を本番LPサイトに反映します。よろしいですか。'
            });
            if (!confirmSync) return;

            this.isPublishing = true;
            try {
                await this.syncLandingPagePublish({ id: this.dataForm.id });
                const detail = await this.getDetailLandingPage(this.dataForm.id);
                this.$set(this.dataForm, 'last_publish_updated', detail?.last_publish_updated || '');
                this.$bvToast.toast('本番LPサイトへの反映が完了しました。', {
                    title: '成功',
                    variant: 'success',
                    toaster: 'b-toaster-top-center',
                    solid: true
                });
            } catch (error) {
                console.error(error);
                showMessage(error?.code || 500, this.$bvToast, error?.message || '更新に失敗しました。');
            } finally {
                this.isPublishing = false;
            }
        },

        getFileName(url) {
            if (!url) return '';
            try {
                const parts = url.split('/');
                return decodeURIComponent(parts[parts.length - 1]);
            } catch {
                return url;
            }
        },

        getFileIcon(url) {
            const lower = url.toLowerCase();
            if (lower.endsWith('.html') || lower.endsWith('.htm')) return 'ri-html5-line';
            if (lower.endsWith('.css')) return 'ri-css3-line';
            if (lower.endsWith('.js')) return 'ri-javascript-line';
            if (
                lower.endsWith('.png') ||
                lower.endsWith('.jpg') ||
                lower.endsWith('.jpeg') ||
                lower.endsWith('.gif') ||
                lower.endsWith('.svg') ||
                lower.endsWith('.webp')
            ) {
                return 'ri-image-line';
            }
            return 'ri-file-line';
        }
    }
};
</script>

<style lang="scss" scoped>
.upload-zip-container {
    .spin {
        animation: spin 1s linear infinite;
    }
    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
}
</style>
